import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import { useState } from 'react';
import { withAlert } from 'react-alert';

import { axiosInstance } from '../../utils/axiosInstance';
import { endpoint } from '../../utils/constants';

const styles = {
    formField: {
        display: 'block',
        marginBottom: '0.5rem',
    },
    actions: {
        padding: '8px 24px 24px',
    },
};

const EMPTY_FORM = {
    current_password: '',
    password: '',
    password_confirmation: '',
};

const EMPTY_TOUCHED_FIELDS = {
    current_password: false,
    password: false,
    password_confirmation: false,
};

const MIN_PASSWORD_LENGTH = 8;

const CHANGE_FAILED_MESSAGE = 'Nie udało się zmienić hasła';

const getCurrentPasswordError = currentPassword => (currentPassword ? '' : 'Obecne hasło jest wymagane');

const getNewPasswordError = password => {
    if (!password) {
        return 'Nowe hasło jest wymagane';
    }

    if (password.length < MIN_PASSWORD_LENGTH) {
        return `Nowe hasło musi mieć co najmniej ${MIN_PASSWORD_LENGTH} znaków`;
    }

    return '';
};

const getConfirmationError = (confirmation, password) => {
    if (!confirmation) {
        return 'Potwierdzenie nowego hasła jest wymagane';
    }

    if (confirmation !== password) {
        return 'Hasła nie są identyczne';
    }

    return '';
};

const getErrors = form => ({
    current_password: getCurrentPasswordError(form.current_password),
    password: getNewPasswordError(form.password),
    password_confirmation: getConfirmationError(form.password_confirmation, form.password),
});

export const ChangePasswordDialog = ({ classes, isOpen, onClose, alert: { show } }) => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [touchedFields, setTouchedFields] = useState(EMPTY_TOUCHED_FIELDS);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const errors = getErrors(form);
    const isFormValid = Object.values(errors).every(error => !error);

    const handleChange = event => {
        const { name, value } = event.target;
        setForm(previousForm => ({ ...previousForm, [name]: value }));
    };

    const handleBlur = event => {
        const { name } = event.target;
        setTouchedFields(previousFields => ({ ...previousFields, [name]: true }));
    };

    const getVisibleError = name => (touchedFields[name] || form[name] ? errors[name] : '');

    const close = () => {
        setForm(EMPTY_FORM);
        setTouchedFields(EMPTY_TOUCHED_FIELDS);
        onClose();
    };

    const getErrorMessage = error => {
        const errorData = error?.response?.data?.error;

        return typeof errorData === 'string' && errorData ? errorData : CHANGE_FAILED_MESSAGE;
    };

    const submit = () => {
        if (!isFormValid) {
            setTouchedFields({
                current_password: true,
                password: true,
                password_confirmation: true,
            });
            return;
        }

        setIsSubmitting(true);

        axiosInstance
            .post(endpoint.USER_CHANGE_PASSWORD, form)
            .then(() => {
                setIsSubmitting(false);
                close();
                show('Hasło zostało zmienione');
            })
            .catch(error => {
                setIsSubmitting(false);
                show(getErrorMessage(error));
            });
    };

    return (
        <Dialog open={isOpen} onClose={close} maxWidth="xs" fullWidth>
            <DialogTitle>Zmień hasło</DialogTitle>
            <DialogContent>
                <TextField
                    id="current-password"
                    className={classes.formField}
                    label="Obecne hasło"
                    name="current_password"
                    type="password"
                    autoComplete="current-password"
                    value={form.current_password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    helperText={getVisibleError('current_password')}
                    error={Boolean(getVisibleError('current_password'))}
                    variant="outlined"
                    required
                    autoFocus
                />
                <TextField
                    id="new-password"
                    className={classes.formField}
                    label="Nowe hasło"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    value={form.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    helperText={getVisibleError('password')}
                    error={Boolean(getVisibleError('password'))}
                    variant="outlined"
                    required
                />
                <TextField
                    id="new-password-confirmation"
                    className={classes.formField}
                    label="Potwierdź nowe hasło"
                    name="password_confirmation"
                    type="password"
                    autoComplete="new-password"
                    value={form.password_confirmation}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    helperText={getVisibleError('password_confirmation')}
                    error={Boolean(getVisibleError('password_confirmation'))}
                    variant="outlined"
                    required
                />
            </DialogContent>
            <DialogActions className={classes.actions}>
                <Button onClick={close} color="primary">
                    Anuluj
                </Button>
                <Button
                    onClick={submit}
                    color="primary"
                    variant="contained"
                    disabled={!isFormValid || isSubmitting}>
                    Zapisz
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default withStyles(styles)(withAlert()(ChangePasswordDialog));
